import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Camera, Loader2 } from 'lucide-react';
import { Button } from '../../shared/ui/Button';
import { Skeleton } from '../../shared/ui/Skeleton';
import { useToast } from '../../shared/ui/Toast';
import { ConfirmModal } from '../../shared/ui/ConfirmModal';
import { useMe, useUpdateMe, useUpdateNotificationPrefs, useUploadAvatar, useDeleteMe } from '../../features/users/hooks';
import { ChangeEmailModal } from '../../features/users/ChangeEmailModal';
import { useSignOut } from '../../features/auth/hooks';
import { useAppStore } from '../../store/useAppStore';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

const AVATAR_ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const AVATAR_MAX_BYTES = 10 * 1024 * 1024; // 10 MB, matches the backend's presigned-upload limit

const schema = z.object({
  name: z.string().min(2, 'Name is required'),
  phone: z.string().optional().refine(
    (v) => !v || /^[+\d][\d\s()-]{6,}$/.test(v),
    { message: 'Enter a valid phone number' },
  ),
  company: z.string().max(100, 'Company name is too long').optional(),
});

type FormData = z.infer<typeof schema>;

const inputClass =
  'w-full rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors';
const labelClass = 'block text-[11px] font-bold text-muted uppercase tracking-widest mb-2';

export function ProfilePage() {
  usePageMeta('Profile');
  const { data: me, isLoading } = useMe();
  const updateMe = useUpdateMe();
  const updateNotificationPrefs = useUpdateNotificationPrefs();
  const uploadAvatar = useUploadAvatar();
  const deleteMe = useDeleteMe();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const { toast } = useToast();
  const user = useAppStore((s) => s.user);
  const [changeEmailOpen, setChangeEmailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (me) reset({ name: me.name, phone: me.phone ?? '', company: me.company ?? '' });
  }, [me, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      await updateMe.mutateAsync(data);
      toast('Profile updated.', 'success');
    } catch {
      toast('Failed to update profile.', 'error');
    }
  };

  const handleAvatarFile = async (file: File | undefined) => {
    if (!file) return;

    if (!AVATAR_ALLOWED_TYPES.has(file.type)) {
      toast('Please choose a JPEG, PNG, or WebP image.', 'error');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast('That file is too large, please choose one under 10 MB.', 'error');
      return;
    }

    try {
      await uploadAvatar.mutateAsync(file);
      toast('Profile photo updated.', 'success');
    } catch {
      toast("Couldn't upload that photo, please try again.", 'error');
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const onConfirmDelete = async () => {
    try {
      await deleteMe.mutateAsync();
      setDeleteOpen(false);
      await signOut();
      navigate('/');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete account.', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col max-w-lg mx-auto w-full">
        <div className="pb-5">
          <Skeleton className="h-3 w-16 mb-2" />
          <Skeleton className="h-7 w-32" />
        </div>
        <div className="py-5 flex items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full flex-shrink-0" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
        <div className="py-5 flex flex-col gap-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-w-lg mx-auto w-full">

      {/* Header */}
      <AnimateIn animation="fade-up">
      <div className="pb-5">
        <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
        <h1 className="text-2xl font-black text-text">Profile</h1>
      </div>
      </AnimateIn>

      {/* Identity */}
      <AnimateIn animation="fade-up" delay={75}>
      <div className="py-5 flex items-center gap-4">
        <button
          type="button"
          onClick={() => avatarInputRef.current?.click()}
          disabled={uploadAvatar.isPending}
          className="group relative h-12 w-12 rounded-full flex-shrink-0 disabled:opacity-70"
          aria-label="Change profile photo"
        >
          {user?.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="h-12 w-12 rounded-full object-cover" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-accent flex items-center justify-center text-black text-lg font-black">
              {user?.avatarInitials ?? <User size={22} />}
            </div>
          )}
          <div className={`absolute inset-0 rounded-full flex items-center justify-center bg-black/50 transition-opacity ${
            uploadAvatar.isPending ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}>
            {uploadAvatar.isPending ? (
              <Loader2 size={16} className="text-white animate-spin" />
            ) : (
              <Camera size={16} className="text-white" />
            )}
          </div>
        </button>
        <input
          ref={avatarInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleAvatarFile(e.target.files?.[0])}
        />
        <div>
          <p className="font-black text-text">{me?.name}</p>
          <p className="text-sm text-muted">{me?.email}</p>
          <span className="inline-flex items-center mt-1 rounded-full bg-surface-raised px-2.5 py-0.5 text-[11px] font-bold text-muted uppercase tracking-widest">
            {me?.role}
          </span>
        </div>
      </div>
      </AnimateIn>

      {/* Edit form */}
      <AnimateIn animation="fade-up" delay={150}>
      <div className="py-5">
        <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-4">Edit details</p>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <div>
            <label className={labelClass}>Full name</label>
            <input className={inputClass} placeholder="Your name" {...register('name')} />
            {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input type="tel" className={inputClass} placeholder="+1 876 XXX XXXX" {...register('phone')} />
            {errors.phone && <p className="text-xs text-danger mt-1">{errors.phone.message}</p>}
          </div>
          <div>
            <label className={labelClass}>Company</label>
            <input className={inputClass} placeholder="Your company (optional)" {...register('company')} />
            {errors.company && <p className="text-xs text-danger mt-1">{errors.company.message}</p>}
          </div>
          <div className="flex justify-end pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={!isDirty}
              loading={updateMe.isPending}
              loadingText="Saving…"
              className="!text-sm normal-case !tracking-normal rounded-xl !px-4 !py-2.5 hover:!bg-accent hover:opacity-90 disabled:!opacity-40"
            >
              Save changes
            </Button>
          </div>
        </form>
      </div>
      </AnimateIn>

      {/* Email (read-only) */}
      <AnimateIn animation="fade-up" delay={225}>
      <div className="py-5">
        <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Email address</p>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-text">{me?.email}</p>
          <Button
            variant="ghost"
            onClick={() => setChangeEmailOpen(true)}
            className="!text-accent !text-xs !font-bold normal-case !tracking-normal !px-0 hover:!bg-transparent hover:underline flex-shrink-0"
          >
            Change email
          </Button>
        </div>
      </div>
      </AnimateIn>

      {/* Notifications */}
      <AnimateIn animation="fade-up" delay={262}>
      <div className="py-5 border-t border-line">
        <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-3">Notifications</p>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm text-text">Job updates</p>
            <p className="text-xs text-muted mt-0.5">Status changes and driver assignment for your requests</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={me?.notificationPrefs?.jobUpdates ?? true}
            aria-label="Job update notifications"
            disabled={updateNotificationPrefs.isPending}
            onClick={() => updateNotificationPrefs.mutate(!(me?.notificationPrefs?.jobUpdates ?? true), {
              onError: () => toast('Could not update preference.', 'error'),
            })}
            className={`group relative inline-flex h-8 w-14 flex-shrink-0 items-center rounded-full border p-1 transition-all disabled:opacity-60 ${
              (me?.notificationPrefs?.jobUpdates ?? true)
                ? 'border-accent bg-accent/15'
                : 'border-line bg-surface-raised'
            }`}
          >
            <span
              className={`inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transition-transform ${
                (me?.notificationPrefs?.jobUpdates ?? true) ? 'translate-x-6' : 'translate-x-0'
              }`}
            >
              {updateNotificationPrefs.isPending ? <span className="h-2 w-2 animate-pulse rounded-full bg-muted" /> : null}
            </span>
          </button>
        </div>
      </div>
      </AnimateIn>

      {/* Danger zone */}
      <AnimateIn animation="fade-up" delay={300}>
      <div className="py-5 border-t border-line mt-2">
        <p className="text-[11px] font-bold text-danger uppercase tracking-widest mb-3">Danger zone</p>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-muted">Permanently delete your account and revoke access.</p>
          <Button
            variant="ghost"
            onClick={() => setDeleteOpen(true)}
            className="!text-danger !text-xs !font-bold normal-case !tracking-normal !px-0 hover:!bg-transparent hover:underline flex-shrink-0"
          >
            Delete account
          </Button>
        </div>
      </div>
      </AnimateIn>

      <ChangeEmailModal open={changeEmailOpen} onClose={() => setChangeEmailOpen(false)} />

      <ConfirmModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={onConfirmDelete}
        title="Delete your account?"
        message="This permanently deletes your account and signs you out. This can't be undone. Any request currently in progress must be delivered or cancelled first."
        confirmLabel="Delete account"
        danger
        loading={deleteMe.isPending}
      />
    </div>
  );
}
